import React from 'react';
import {Box, Button, CircularProgress} from "@mui/material";
import {Chip, Typography} from "@mui/joy";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {CheckCircleRounded, WarningRounded} from "@mui-symbols-material/w300";
import PrintDeclarationButton from "../ContaminationDeclarationStatus/PrintDeclarationButton";

interface Props {
    declarationId: string;
    /** Printable *and* nothing unsaved — computed once by the form, shared with the sidebar. */
    ready: boolean;
    /** Why printing is blocked; shown as the button's tooltip. */
    printBlockedReason: string;
    /** Current form value of the consent box (live, not the saved one). */
    declarationAccepted: boolean;
    /** Live count of required, active, unanswered questions. */
    openCount: number;
    updatedAt?: string | null;
    updatedByName?: string | null;
    canUpdate: boolean;
    dirty: boolean;
    submitting: boolean;
}

/**
 * Sticky footer: print readiness, the responsible person the printed form will
 * name, the Print button and the single Save button. Saving is always allowed —
 * completeness is reported, never enforced. Floats as a white bar over the grey
 * page surface while the sections scroll.
 */
const StatusFooter = ({declarationId, ready, printBlockedReason, declarationAccepted, openCount, updatedAt, updatedByName, canUpdate, dirty, submitting}: Props) => {
    const {t} = useTranslation();

    return (
        <Box
            data-testid="doc-status"
            sx={{
                position: 'sticky', bottom: '12px', zIndex: 2, mt: '32px',
                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                p: '12px 16px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fff',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
            }}
        >
            <Box sx={{flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: '2px'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                    {ready ? (
                        <Chip variant="soft" color="success" size="sm" startDecorator={<CheckCircleRounded fontSize="small"/>}>
                            {t('Ready to print')}
                        </Chip>
                    ) : (
                        <Chip variant="soft" color="warning" size="sm" startDecorator={<WarningRounded fontSize="small"/>}>
                            {openCount > 0
                                ? t('Open answers: {{count}}', {count: openCount})
                                : t('Not ready to print')}
                        </Chip>
                    )}
                    {/* The consent tick counts as an open answer; name it once it is the only one left. */}
                    {!ready && openCount <= 1 && !declarationAccepted && (
                        <Typography level="body-xs" sx={{color: 'text.tertiary'}}>
                            {t('The legally binding declaration has not been accepted yet.')}
                        </Typography>
                    )}
                    {dirty && (
                        <Typography level="body-xs" sx={{color: 'text.tertiary'}}>{t('Unsaved changes')}</Typography>
                    )}
                </Box>
                <Typography level="body-xs" sx={{color: 'text.tertiary'}}>
                    {updatedAt
                        ? `${t('Responsible person')}: ${updatedByName || '–'} · ${moment(updatedAt).format('L LT')}`
                        : t('Not yet edited. The last editor is named as the responsible person on the printed form.')}
                </Typography>
            </Box>

            {/* Outside the canUpdate gate: printing is a read, and a read-only
                viewer is often exactly the person who needs the paper copy. */}
            <PrintDeclarationButton
                declarationId={declarationId}
                disabled={!ready}
                tooltip={ready ? undefined : printBlockedReason}
                size="medium"
                data-testid="doc-print"
            />

            {canUpdate && (
                <Button
                    type="submit"
                    variant="contained"
                    disabled={!dirty || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit"/> : undefined}
                    data-testid="doc-save"
                >
                    {t('Save')}
                </Button>
            )}
        </Box>
    );
};

export default StatusFooter;
